import { useRef } from 'react';

import { Input, RadioGroup, RadioGroupItem, cn } from '@design-system';

import type { AnswerOption } from '@shared';

interface QuestionBodySingleSelectProps {
  options: AnswerOption[];
  value: string | null;
  onChange: (value: string) => void;
  otherValue?: string;
  onOtherValueChange?: (value: string) => void;
  disabled?: boolean;
}

const OTHER_VALUE = 'other';

export function QuestionBodySingleSelect({
  options,
  value,
  onChange,
  otherValue,
  onOtherValueChange,
  disabled,
}: QuestionBodySingleSelectProps) {
  return (
    <RadioGroup
      value={value ?? ''}
      onValueChange={onChange}
      disabled={disabled}
      className="flex flex-col gap-3"
    >
      {options.map(option => (
        <SingleSelectRow
          key={option.value}
          option={option}
          isSelected={value === option.value}
          otherValue={otherValue}
          onOtherValueChange={onOtherValueChange}
          onSelect={onChange}
          disabled={disabled}
        />
      ))}
    </RadioGroup>
  );
}

interface SingleSelectRowProps {
  option: AnswerOption;
  isSelected: boolean;
  otherValue: string | undefined;
  onOtherValueChange: ((value: string) => void) | undefined;
  onSelect: (value: string) => void;
  disabled?: boolean;
}

function SingleSelectRow({
  option,
  isSelected,
  otherValue,
  onOtherValueChange,
  onSelect,
  disabled,
}: SingleSelectRowProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const isOther = option.value === OTHER_VALUE;
  const id = `single-${option.value}`;

  if (isOther && isSelected && onOtherValueChange) {
    return (
      <div
        className="border-primary/30 bg-primary/10 bg-background flex w-full items-center justify-between gap-3 rounded-md border p-4 transition-colors"
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        <Input
          ref={inputRef}
          autoFocus
          placeholder="Please specify"
          value={otherValue ?? ''}
          onChange={e => onOtherValueChange(e.target.value)}
          onClick={e => e.stopPropagation()}
          disabled={disabled}
          className="h-auto flex-1 border-0 bg-transparent p-0 text-sm font-medium text-gray-950 shadow-none focus-visible:ring-0 md:text-sm"
        />
        <RadioGroupItem
          id={id}
          value={option.value}
          disabled={disabled}
          onClick={e => e.stopPropagation()}
          className="size-5 rounded-full"
        />
      </div>
    );
  }

  return (
    <label
      htmlFor={id}
      className={cn(
        'border-border bg-background flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border p-4 text-left transition-colors',
        'hover:border-primary/30',
        {
          'border-primary/30 bg-primary/10': isSelected,
          'cursor-not-allowed opacity-50': disabled,
        },
      )}
      onClick={e => {
        if (disabled) return;
        e.preventDefault();
        onSelect(option.value);
      }}
    >
      <span className="text-sm font-medium text-gray-950">{option.label}</span>
      <RadioGroupItem
        id={id}
        value={option.value}
        disabled={disabled}
        className="size-5 rounded-full"
      />
    </label>
  );
}
